
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { API_ENDPOINT, getAuthHeader } from "@/config/environment";
import { UserSubscriptions } from "@/components/subscriptions/UserSubscriptions";
import { BillsTable } from "@/components/subscriptions/BillsTable";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { LoaderCircle, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { CancellationDialog } from "@/components/subscriptions/CancellationDialog";

const PublicSubscriptions = () => {
  const [searchParams] = useSearchParams();
  const schoolId = searchParams.get('schoolId');
  const token = searchParams.get('token');
  const [clientName, setClientName] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("subscriptions");
  const [dataFetched, setDataFetched] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    // Only fetch data once
    if (dataFetched) return;
    
    const fetchSchoolData = async () => {
      try {
        setLoading(true);
        
        // Validate input parameters
        if (!schoolId) {
          setError("Missing school ID parameter");
          toast({
            variant: "destructive",
            title: "Error",
            description: "Missing required parameters",
          });
          setLoading(false);
          return;
        }

        // Prepare headers with token if available
        const headers = getAuthHeader();
        
        // Construct the URL with token if available - using GET request
        let url = `${API_ENDPOINT}/get-school-subscriptions?schoolId=${encodeURIComponent(schoolId)}`;
        if (token) {
          url += `&token=${encodeURIComponent(token)}`;
        }

        console.log('Fetching school data from:', url);
        
        // Direct call to the edge function
        const response = await fetch(url, { 
          method: 'GET',
          headers 
        });

        if (!response.ok) {
          let errorMessage = 'Failed to load subscription data';
          try {
            const errorData = await response.json();
            console.error('Error response from API:', errorData);
            errorMessage = errorData.error || errorMessage;
          } catch (e) {
            console.error('Could not parse error response:', e);
          }
          setError(`API error: ${errorMessage}`);
          toast({
            variant: "destructive",
            title: "Error",
            description: errorMessage,
          });
          setLoading(false);
          return;
        }

        const data = await response.json();
        if (!data.success) {
          setError(data.error || "Unknown error");
          toast({
            variant: "destructive",
            title: "Error",
            description: data.error || "Failed to load subscription data",
          });
          setLoading(false);
          return;
        }

        // Set client name for display
        setClientName(data.data.school.nama_sekolah || data.data.school.name);
        setDataFetched(true);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching school data:', err);
        setError(`An unexpected error occurred: ${err.message}`);
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to load subscription data",
        });
        setLoading(false);
      }
    };

    fetchSchoolData();
  }, [schoolId, token, toast, dataFetched]);

  const handleTabChange = (value: string) => {
    setActiveTab(value);
  };

  if (loading) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-gray-100">
        <LoaderCircle className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-gray-100">
        <div className="bg-white p-8 rounded-lg shadow-md text-center max-w-md">
          <div className="flex justify-center mb-4">
            <ShieldAlert className="h-12 w-12 text-red-500" />
          </div>
          <h2 className="text-2xl font-semibold text-red-600 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <p className="text-gray-500 mb-6 text-sm">
            This page requires a valid access token. Please contact the school administrator to get a secure access link.
          </p>
          <Button 
            onClick={() => navigate('/')}
            className="w-full"
          >
            Return to Homepage
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-gray-100 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold" style={{ color: "#008169" }}>
            Manage Your Subscriptions
          </h1>
          {clientName && <p className="text-lg text-gray-600 mt-1">{clientName}</p>}
        </div>
        
        <div className="rounded-lg shadow-sm bg-white">
          <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
            <TabsList className="border-b border-gray-200 w-full rounded-none h-auto p-0">
              <div className="px-6 pt-4">
                <TabsTrigger value="subscriptions" className="data-[state=active]:border-b-2 data-[state=active]:border-teal-600 data-[state=active]:text-teal-600 rounded-none border-b-2 border-transparent px-4 py-2 bg-transparent">
                  Subscriptions
                </TabsTrigger>
                <TabsTrigger value="bills" className="data-[state=active]:border-b-2 data-[state=active]:border-teal-600 data-[state=active]:text-teal-600 rounded-none border-b-2 border-transparent px-4 py-2 bg-transparent">
                  Bills
                </TabsTrigger>
              </div>
            </TabsList>
            <TabsContent value="subscriptions" className="p-6 bg-transparent">
              <UserSubscriptions schoolId={schoolId} isPublicView={true} />
            </TabsContent>
            <TabsContent value="bills" className="p-6">
              <BillsTable schoolId={schoolId} isPublicView={true} />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}

export default PublicSubscriptions;
